import { Label, RadioGroup, RadioGroupItem } from '@startupquickstart/frontend';
import { Percent, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';

export type DiscountType = 'percentage' | 'fixed_amount';

export interface DiscountTypeSelectorProps {
  value?: DiscountType;
  onChange: (value: DiscountType) => void;
}

const discountOptions = [
  {
    value: 'percentage' as const,
    icon: Percent,
    title: 'Percentage Off',
    description: 'Discount as a percentage of the total',
  },
  {
    value: 'fixed_amount' as const,
    icon: DollarSign,
    title: 'Fixed Amount',
    description: 'Discount as a fixed dollar amount',
  },
];

export function DiscountTypeSelector({ value, onChange }: DiscountTypeSelectorProps) {
  return (
    <RadioGroup
      value={value ?? 'percentage'}
      onValueChange={(val) => onChange(val as DiscountType)}
      className="grid gap-3 sm:grid-cols-2"
    >
      {discountOptions.map((option) => {
        const Icon = option.icon;
        const isSelected = value === option.value || (!value && option.value === 'percentage');

        return (
          <Label
            key={option.value}
            htmlFor={`discount-${option.value}`}
            className={cn(
              'flex cursor-pointer items-start gap-4 rounded-xl border p-4 transition-all',
              'hover:border-primary/50 hover:bg-muted/50',
              isSelected
                ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                : 'border-border bg-card',
            )}
          >
            <RadioGroupItem
              value={option.value}
              id={`discount-${option.value}`}
              className="mt-0.5 shrink-0"
            />
            <div className="flex flex-1 items-start gap-3">
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                  isSelected
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 space-y-1">
                <div className={cn('font-medium', isSelected && 'text-primary')}>
                  {option.title}
                </div>
                <div className="text-sm text-muted-foreground">{option.description}</div>
              </div>
            </div>
          </Label>
        );
      })}
    </RadioGroup>
  );
}
